import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection } from 'constants/index'
import { signOut } from 'store/auth'

const PAGE_SIZE = 50

export const initialState = {
  error: null,
  inProgress: false,
  logs: [],
  log: null,
  typeFilter: 'ALL',
  // last document snapshot of the current page, used as the startAfter cursor.
  // Kept out of the serializable check (store sets serializableCheck: false).
  cursor: null,
  hasMore: true,
  pageSize: PAGE_SIZE
}

const notificationLogsSlice = createSlice({
  name: 'notificationLogs',
  initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setLogs: (state, action) => {
      const { logs, reset, cursor, hasMore } = action.payload
      state.logs = reset ? logs : [...state.logs, ...logs]
      state.cursor = cursor
      state.hasMore = hasMore
    },
    setLog: (state, action) => {
      state.log = action.payload
    },
    setTypeFilter: (state, action) => {
      state.typeFilter = action.payload
      // a new filter starts a fresh page set
      state.logs = []
      state.cursor = null
      state.hasMore = true
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.logs = []
      state.log = null
      state.typeFilter = 'ALL'
      state.cursor = null
      state.hasMore = true
      state.pageSize = PAGE_SIZE
    }
  }
})

export const fetchNotificationLogs =
  ({ reset = false } = {}) =>
  async (dispatch, getState) => {
    try {
      const { auth, notificationLogs } = getState()
      const conferenceId = auth.user?.conferenceId
      if (!conferenceId) return

      const { typeFilter, cursor, pageSize } = notificationLogs

      dispatch(notificationLogsSlice.actions.setProgress(true))

      // Always scope to the admin's conference — never list across conferences.
      let query = FirestoreDatabase.collection(Collection.NOTIFICATION_LOGS).where(
        'conferenceId',
        '==',
        conferenceId
      )

      if (typeFilter && typeFilter !== 'ALL') {
        query = query.where('notificationType', '==', typeFilter)
      }

      query = query.orderBy('createdAt', 'desc').limit(pageSize)

      if (!reset && cursor) {
        query = query.startAfter(cursor)
      }

      const snapshot = await query.get()

      // Skip legacy docs that use the older { kind: 'summary' | 'recipient' } shape.
      const logs = snapshot.docs
        .filter((doc) => !doc.data()?.kind)
        .map((doc) => ({ id: doc.id, ...doc.data() }))

      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null

      dispatch(
        notificationLogsSlice.actions.setLogs({
          logs,
          reset,
          cursor: lastDoc,
          hasMore: snapshot.docs.length === pageSize
        })
      )
    } catch (err) {
      console.error('Error fetching notification logs:', err)
      dispatch(notificationLogsSlice.actions.setError(err))
    } finally {
      dispatch(notificationLogsSlice.actions.setProgress(false))
    }
  }

export const fetchNotificationLogById = (logId) => async (dispatch) => {
  try {
    dispatch(notificationLogsSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.NOTIFICATION_LOGS)
      .doc(logId)
      .get()
    dispatch(
      notificationLogsSlice.actions.setLog(
        snapshot.exists ? { id: snapshot.id, ...snapshot.data() } : null
      )
    )
  } catch (err) {
    dispatch(notificationLogsSlice.actions.setError(err))
  } finally {
    dispatch(notificationLogsSlice.actions.setProgress(false))
  }
}

export const setTypeFilter = (type) => (dispatch) => {
  dispatch(notificationLogsSlice.actions.setTypeFilter(type))
}

// Recipients are stored inline on the doc (successfulRecipients / failedRecipients),
// so opening a log just selects the already-loaded document — no extra fetch.
export const selectLog = (log) => (dispatch) => {
  dispatch(notificationLogsSlice.actions.setLog(log))
}

export const resetNotificationLog = () => (dispatch) => {
  dispatch(notificationLogsSlice.actions.setLog(null))
}

export default notificationLogsSlice
